import { useState } from "react";
import { CheckCircle, Copy } from "react-feather";
import { toast } from "react-hot-toast";
import { buildJoinPortalUrl } from "../../utils/programShareLink";

interface CopyProgramLinkButtonProps {
  /** Brand username — the `/store/<username>/join` part of the link. */
  username?: string;
  /** The program's share token. Without one there is no link to copy. */
  shareToken?: string;
  /** Shown in the toast so a brand knows which link landed on their clipboard. */
  programLabel?: string;
  variant?: "icon" | "button";
  className?: string;
}

/**
 * Copies one program's shareable join link.
 *
 * The link takes a visitor to that specific program rather than the brand's
 * discoverable one — whether they end up enrolled or merely applied depends on
 * the program's `automaticApproval`. See docs/PROGRAM_SHARE_LINKS.md. For a
 * control that offers every program at once, use `JoinPortalLinkPopover`.
 */
export function CopyProgramLinkButton({
  username,
  shareToken,
  programLabel,
  variant = "icon",
  className = "",
}: CopyProgramLinkButtonProps) {
  const [copied, setCopied] = useState(false);

  // A program that predates share tokens gets one the first time the brand
  // loads its program list, so this is a transient state at worst.
  const isDisabled = !username || !shareToken;

  const handleCopy = async (event: React.MouseEvent) => {
    // These live inside clickable table rows — don't trigger the row's navigate.
    event.stopPropagation();
    if (isDisabled) return;

    try {
      await navigator.clipboard.writeText(
        buildJoinPortalUrl(username, shareToken)
      );
      setCopied(true);
      toast.success(
        programLabel
          ? `Join link for ${programLabel} copied!`
          : "Join link copied to clipboard!"
      );
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const label = copied ? "Copied" : "Copy join link";

  if (variant === "button") {
    return (
      <button
        type="button"
        onClick={handleCopy}
        disabled={isDisabled}
        title={isDisabled ? "This program has no join link yet" : label}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
          copied
            ? "border-green-200 bg-green-50 text-green-700"
            : "border-slate-300 text-slate-700 hover:bg-slate-50"
        } ${className}`}
      >
        {copied ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        <span>{label}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      disabled={isDisabled}
      aria-label={label}
      title={isDisabled ? "This program has no join link yet" : label}
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        copied
          ? "text-green-600"
          : "text-brand-600 hover:bg-brand-50 hover:text-brand-700"
      } ${className}`}
    >
      {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      <span className="hidden xl:inline">{copied ? "Copied" : "Copy link"}</span>
    </button>
  );
}

export default CopyProgramLinkButton;
